import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Heading,
    Text,
    SimpleGrid,
    VStack,
    HStack,
    Icon,
    Spinner,
    useToast,
    Badge,
    Flex,
    Avatar,
    Button,
    Divider
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FaIcons from 'react-icons/fa';
import { FaGraduationCap, FaArrowLeft, FaBookOpen, FaLayerGroup } from 'react-icons/fa';
import niveauService from '../services/niveauService';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const MotionBox = motion(Box);

const CourseDiscovery = () => {
    const [niveaux, setNiveaux] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const toast = useToast();

    const primaryColor = '#F2C919';
    const secondaryColor = '#2C3E50';

    useEffect(() => {
        fetchNiveaux();
    }, []);

    const fetchNiveaux = async () => {
        try {
            const response = await niveauService.getNiveaux();
            setNiveaux(response.data);
        } catch (error) {
            toast({
                title: 'خطأ في تحميل المستويات',
                description: 'تعذر الاتصال بالخادم، يرجى المحاولة لاحقاً.',
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'top'
            });
        } finally {
            setLoading(false);
        }
    };

    const getIconComponent = (iconName) => {
        // Fallback to FaIcons if iconName is found, otherwise graduation cap
        return FaIcons[iconName] || FaGraduationCap;
    };

    if (loading) {
        return (
            <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
                <Spinner size="xl" color={primaryColor} thickness="4px" />
            </Box>
        );
    }

    return (
        <Box dir="rtl" bg="gray.50" minH="100vh">
            <Header />

            <Container maxW="1400px" py={20}>
                {/* Section Header */}
                <VStack spacing={4} mb={16} textAlign="center">
                    <Heading size="2xl" color={secondaryColor} fontFamily="Tajawal, sans-serif" fontWeight="900">
                        قائمة المستويات الدراسية
                    </Heading>
                    <Text fontSize="lg" color="gray.500" maxW="2xl" fontFamily="Tajawal, sans-serif" fontWeight="600">
                        اضغط على مستواك الدراسي لاستكشاف المواد والدروس المتاحة.
                    </Text>
                </VStack>

                {/* Niveaux Grid */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={10}>
                    {niveaux.map((niveau, index) => {
                        const IconComponent = getIconComponent(niveau.icon);

                        return (
                            <MotionBox
                                key={niveau._id}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                whileHover={{ y: -10 }}
                                onClick={() => navigate(`/discover/${niveau._id}`)}
                                cursor="pointer"
                                h="100%"
                            >
                                <Box
                                    bg="white"
                                    boxShadow="xl"
                                    borderRadius="3xl"
                                    overflow="hidden"
                                    border="2px solid"
                                    borderColor="gray.100"
                                    transition="all 0.3s"
                                    _hover={{ borderColor: primaryColor, boxShadow: '2xl' }}
                                    h="100%"
                                    display="flex"
                                    flexDirection="column"
                                >
                                    <VStack p={10} spacing={6} align="stretch" flex={1}>
                                        <VStack spacing={4} align="center">
                                            <Box
                                                p={6}
                                                bg={`${niveau.color || primaryColor}15`}
                                                borderRadius="2xl"
                                                color={niveau.color || secondaryColor}
                                            >
                                                <Icon as={IconComponent} boxSize={10} />
                                            </Box>
                                            <VStack spacing={2}>
                                                <Heading size="lg" color={secondaryColor} fontFamily="Tajawal, sans-serif" fontWeight="900" textAlign="center">
                                                    {niveau.name}
                                                </Heading>
                                                <Badge
                                                    colorScheme="yellow"
                                                    bg={primaryColor}
                                                    color={secondaryColor}
                                                    px={4}
                                                    py={1}
                                                    borderRadius="full"
                                                    fontSize="sm"
                                                    fontWeight="800"
                                                    fontFamily="Tajawal, sans-serif"
                                                >
                                                    {niveau.matieres?.length || 0} مادة تعليمية
                                                </Badge>
                                            </VStack>
                                        </VStack>

                                        <Divider borderColor="gray.100" />

                                        <VStack align="flex-start" spacing={4} flex={1}>
                                            <HStack spacing={2}>
                                                <Icon as={FaBookOpen} color={primaryColor} />
                                                <Text fontWeight="800" fontFamily="Tajawal, sans-serif" color="gray.700" fontSize="sm">
                                                    عينة من المواد:
                                                </Text>
                                            </HStack>
                                            <VStack align="stretch" w="100%" spacing={2}>
                                                {niveau.matieres?.slice(0, 3).map((matiere, idx) => (
                                                    <HStack key={idx} p={3} bg="gray.50" borderRadius="xl" _hover={{ bg: 'gray.100' }}>
                                                        <Icon as={FaLayerGroup} color="gray.600" boxSize={3} />
                                                        <Text fontSize="sm" fontFamily="Tajawal, sans-serif" fontWeight="700" color="gray.600">
                                                            {matiere.name}
                                                        </Text>
                                                    </HStack>
                                                ))}
                                                {niveau.matieres?.length > 3 && (
                                                    <Text fontSize="xs" color="gray.600" textAlign="center" fontWeight="700" pt={2}>
                                                        + {niveau.matieres.length - 3} مواد إضافية أخرى
                                                    </Text>
                                                )}
                                            </VStack>
                                        </VStack>

                                        <Button
                                            mt={6}
                                            w="100%"
                                            h="60px"
                                            bg={secondaryColor}
                                            color="white"
                                            _hover={{ bg: 'black', transform: 'scale(1.02)' }}
                                            borderRadius="2xl"
                                            fontFamily="Tajawal, sans-serif"
                                            fontWeight="900"
                                            rightIcon={<Icon as={FaArrowLeft} mr={2} boxSize={4} />}
                                        >
                                            استكشف المواد
                                        </Button>
                                    </VStack>
                                </Box>
                            </MotionBox>
                        );
                    })}
                </SimpleGrid>

                {niveaux.length === 0 && (
                    <Box textAlign="center" py={20}>
                        <Icon as={FaGraduationCap} boxSize={20} color="gray.700" mb={4} />
                        <Heading size="md" color="gray.500" fontFamily="Tajawal, sans-serif">لا توجد محتويات متاحة حالياً</Heading>
                    </Box>
                )}

                {/* Bottom CTA Section */}
                <Box
                    mt={32}
                    bgGradient={`linear(to-l, ${primaryColor}, #E0B817)`}
                    borderRadius="3xl"
                    p={{ base: 10, md: 16 }}
                    textAlign="center"
                    boxShadow="2xl"
                >
                    <VStack spacing={8}>
                        <Heading size="xl" color={secondaryColor} fontFamily="Tajawal, sans-serif" fontWeight="900">
                            هل تريد باقة تعليمية متكاملة؟
                        </Heading>
                        <Text fontSize="xl" color={secondaryColor} maxW="3xl" fontFamily="Tajawal, sans-serif" fontWeight="700">
                            اكتشف عروضنا الحصرية التي تشمل حصص مباشرة، تسجيلات، وملخصات لضمان أعلى النتائج.
                        </Text>
                        <Button
                            size="xl"
                            h="70px"
                            px={12}
                            bg={secondaryColor}
                            color="white"
                            _hover={{ bg: 'black', transform: 'scale(1.05)' }}
                            fontFamily="Tajawal, sans-serif"
                            fontWeight="900"
                            onClick={() => navigate('/packs')}
                            borderRadius="2xl"
                            boxShadow="xl"
                        >
                            عرض عروضنا التعليمية
                        </Button>
                    </VStack>
                </Box>
            </Container>

            <Footer />
        </Box>
    );
};

export default CourseDiscovery;

